/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import { useCallback, useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from '@apollo/react-hooks';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { usePageContentId } from '@confluence/page-context';

import type {
	usePublishModalAnalyticsEventPublishModalWithMoreOptionsQuery as usePublishModalAnalyticsEventPublishModalWithMoreOptionsQuery$data,
	usePublishModalAnalyticsEventPublishModalWithMoreOptionsQueryVariables as usePublishModalAnalyticsEventPublishModalWithMoreOptionsQuery$variables,
} from './__types__/usePublishModalAnalyticsEventPublishModalWithMoreOptionsQuery.ts';

type AnalyticEventData = {
	objectId?: string;
	objectType?: string;
	containerId?: string;
};

export const usePublishModalAnalyticsEvent = () => {
	const [contentId] = usePageContentId();
	const { createAnalyticsEvent: akCreateAnalyticsEvent } = useAnalyticsEvents();

	/**
	 * cache-only since it is utilizing the same query that the PublishButtonWithMoreOptions component
	 * is using, so the data should already be available within the cache which is why there is no
	 * error or loading checks as well.
	 */
	const { data } = useQuery<
		usePublishModalAnalyticsEventPublishModalWithMoreOptionsQuery$data,
		usePublishModalAnalyticsEventPublishModalWithMoreOptionsQuery$variables
	>(
		gql`
			query usePublishModalAnalyticsEventPublishModalWithMoreOptionsQuery($contentId: ID) {
				singleContent(id: $contentId) {
					id
					type
					space {
						id
					}
					schedulePublishInfo {
						date
						versionComment
						minorEdit
						targetType
						targetLocation {
							parentId
							destinationSpace {
								id
								key
								alias
							}
						}
						restrictions {
							update {
								user: personConnection {
									edges {
										node {
											email
											permissionType
											displayName
											accountId
											publicName
											profilePicture {
												path
											}
										}
									}
								}
								group {
									edges {
										node {
											name
											id
										}
									}
								}
							}
							read {
								user: personConnection {
									edges {
										node {
											email
											permissionType
											displayName
											accountId
											publicName
											profilePicture {
												path
											}
										}
									}
								}
								group {
									edges {
										node {
											name
											id
										}
									}
								}
							}
						}
					}
				}
			}
		`,
		{
			errorPolicy: 'all',
			fetchPolicy: 'cache-only',
			variables: {
				contentId,
			},
		},
	);

	// Setup the rest of the necessary info for our analytics events that came from the query
	const analyticEventData = useMemo<AnalyticEventData>(() => {
		return {
			objectId: contentId,
			objectType: data?.singleContent?.type ?? undefined,
			containerId: data?.singleContent?.space?.id ?? undefined,
		};
	}, [data, contentId]);

	const createAnalyticsEvent = useCallback(
		(payload: AnalyticsEventPayload) => {
			payload.data.objectType = analyticEventData.objectType;
			payload.data.objectId = analyticEventData.objectId;
			payload.data.containerType = 'space';
			payload.data.containerId = analyticEventData.containerId;
			return akCreateAnalyticsEvent(payload);
		},
		[analyticEventData, akCreateAnalyticsEvent],
	);
	return { createAnalyticsEvent };
};
